import { CreateCheckoutSessionResponse } from '@adyen/api-library/lib/src/typings/checkout/createCheckoutSessionResponse'
import { useMutation } from '@apollo/client/react'
import { useState } from 'react'
import { AppTheme, useTheme } from 'styled-components'

import { FormUserDataType } from '@components/molecules/Forms/FormUserData/FormUserData'
import {
  FormPaymentType,
  PaymentMethod,
} from '@components/molecules/Payment/Payment'

import { parseCreateRegistrationResponse } from 'api/symfony/parsers/graphqlParsers'

import {
  Contract,
  CreateRegistrationInput,
  Mutation,
  Studio,
} from '@definitions/types/symfonyTypes'

import { CREATE_REGISTRATION_MUTATION } from '../api/symfony/graphql/mutations/createRegistration'

type callbackSuccessType = (transactionId: string) => void
type callbackErrprType = () => void

interface useHandleRegistrationReturnType {
  handleRegistration: (
    formUserData: FormUserDataType,
    formPaymentData: FormPaymentType,
    studio: Studio,
    contract: Contract,
    paymentSession: CreateCheckoutSessionResponse | null
  ) => Promise<void>
  handleRegistrationError: Error | null
  error: string | null
}

const useHandleRegistration = (
  onCompleteCallback: callbackSuccessType | null,
  onErrorCallback: callbackErrprType | null
): useHandleRegistrationReturnType => {
  const [handleRegistrationError, setHandleRegistrationError] =
    useState<Error | null>(null)
  const [error, setError] = useState<string | null>(null)

  const theme = useTheme() as AppTheme

  const [createRegistrationMutation] = useMutation<Mutation>(
    CREATE_REGISTRATION_MUTATION
  )

  const handleRegistration = async (
    formUserData: FormUserDataType,
    formPaymentData: FormPaymentType,
    studio: Studio,
    contract: Contract,
    paymentSession: CreateCheckoutSessionResponse | null
  ): Promise<void> => {
    const input: CreateRegistrationInput = {
      firstname: formUserData.vorname,
      lastname: formUserData.nachname,
      street: formUserData.strasse,
      streetNumber: formUserData.nr,
      city: formUserData.ort,
      zipcode: formUserData.zip,
      salutation: formUserData.anrede,
      birthday: formUserData.geburtstag,
      email: formUserData.email,
      phone: formUserData.telefon,
      vouchercode: formUserData.gutschein,
      clubCardReceive: formUserData.recievevia,
      debitIban:
        formPaymentData.paymentmethod === PaymentMethod.SEPA
          ? formPaymentData?.iban ?? null
          : null,
      debitAccountOwner: formPaymentData?.sameperson
        ? `${formUserData.vorname} ${formUserData.nachname}`
        : formPaymentData?.name ?? null,
      debitDirectmandate: formPaymentData?.directdebitmandate ?? false,
      debitSameperson: formPaymentData?.sameperson ?? false,
      paymentTime: formPaymentData?.debitdate ?? 'first',
      paymentType: formPaymentData.paymentmethod,
      contract: contract.id,
      studio: studio.id,
      advertisingPermitted: formUserData.newsletter ?? false,
      affiliationName: theme.key,
    }

    setError(null)
    setHandleRegistrationError(null)

    if (paymentSession) {
      input.paymentReference = paymentSession.reference
      input.shopperReference = paymentSession.shopperReference
      input.adyenMerchantAccount = paymentSession.merchantAccount
    }

    try {
      const { data } = await createRegistrationMutation({
        variables: {
          input,
        },
      })

      if (
        data &&
        'createRegistration' in data &&
        data.createRegistration &&
        onCompleteCallback
      ) {
        const parsed = parseCreateRegistrationResponse(data.createRegistration)
        onCompleteCallback(parsed.id)
      }
    } catch (error) {
      const err = error as Error
      setHandleRegistrationError(err)
      if (onErrorCallback) {
        onErrorCallback()
      }

      if (
        err.message.indexOf(
          'This is not a valid International Bank Account Number'
        ) >= 0
      ) {
        setError('Die IBAN ist nicht gültig')
      } else {
        setError('Es ist Fehler aufgetreten')
      }

      console.error(err)
    }
  }

  return {
    handleRegistration,
    handleRegistrationError,
    error,
  }
}

export default useHandleRegistration
